import React, { Fragment, useEffect, useMemo, } from 'react';
import { sortBy, isEmpty, pick, fromPairs, chunk, } from 'lodash';
import { Link } from 'react-router-dom';
import { Button } from 'reactstrap';
import { setDoc, updateDoc, limit, query, where, orderBy, collection, doc, } from 'firebase/firestore';
import { httpsCallable, } from 'firebase/functions';
import { format as formatDate, addMonths, addDays, startOfDay, endOfDay, startOfMonth, } from 'date-fns';
import numeral from 'numeral';
import { keyBy } from 'lodash';
import { toast } from 'react-toastify';
import qs from 'qs';

import firebase, { db, batch, functions, getCollectionData, } from '../../firebase';
import env from '../../env';
import { fields, behaviors, types, ownerTypes, } from '../../shared/models/expense';
import useQueryParams from '../hooks/useQueryParams';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import ModelFormModal from '../modals/ModelFormModal';
import QueryDateRangeSelector from '../QueryDateRangeSelector';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import AddButton from '../AddButton';
import QueryDateSelector from '../QueryDateSelector';
import ProgressButton from '../ProgressButton';
import TenantPage from '../TenantPage';
import useTenant from '../hooks/useTenant';
import ExpenseTabs from '../ExpenseTabs';
import ExportButton from '../ExportButton';
import ImportMappingButton from '../ImportMappingButton';

const { entries, keys } = Object;
const getAdApiAuthUrl = httpsCallable(functions, 'getAdApiAuthUrl');
const pullAdProfiles = httpsCallable(functions, 'pullAdProfiles');
const costBehaviors = {
  variable: {
    label: '変動費',
    getExpenses: _ => _.variableExpenses
  },
  fixed: {
    label: '固定費',
    getExpenses: _ => _.fixedExpenses,
  },
};

export default function Purchases (props) {
  return (
    <TenantPage>
      <PageContent {...props} />
    </TenantPage>
  );
};

function PageContent (props) {
  const { tenant, history, location, } = props;
  const queryParams = useQueryParams();
  const { month = formatDate(new Date(), 'yyyy-MM'), } = queryParams;
  const sellers = useCollectionSubscription(query(collection(tenant.ref, 'sellers')));
  const shops = useCollectionSubscription(query(collection(tenant.ref, 'shops')));
  const ownersById = keyBy([
    ...sellers.map(_ => ({ ..._, ownerType: 'seller' })),
    ...shops.map(_ => ({ ..._, ownerType: 'shop' })),
  ], 'id');
  const rawExpenses = useCollectionSubscription(query(collection(tenant.ref, 'expenses'), where('month', '==', month)), [month]);
  // 未移行データ（旧sellerIdフィールド）のフォールバック。マイグレーション後は削除
  const expenses = rawExpenses.map(_ => ({
    ..._,
    ownerType: _.ownerType ?? (_.sellerId != null ? 'seller' : undefined),
    ownerId: _.ownerId ?? _.sellerId,
  }));
  const sortedExpenses = sortBy(expenses, [
    _ => keys(ownerTypes).indexOf(_.ownerType),
    'ownerId',
    _ => keys(behaviors).indexOf(_.behavior),
    _ => keys(types).indexOf(_.type),
  ]);

  const getExportData = () => {
    return sortedExpenses.map(_ => ({
      ownerType: ownerTypes[_.ownerType]?.label,
      ownerId: _.ownerId,
      ownerName: ownersById[_.ownerId]?.name,
      behavior: behaviors[_.behavior]?.label,
      type: types[_.type]?.label,
      description: _.description,
      amount: _.amount,
    }));
  };

  const importFields = {
    ownerType: {
      label: '種別',
      type: 'select',
      options: entries(ownerTypes).map(([k, v]) => v.label),
      validations: { required: true },
    },
    ownerId: {
      label: 'ショップID',
      type: 'select',
      options: [...sellers.map(_ => _.id), ...shops.map(_ => _.id)],
      validations: { required: true },
    },
    behavior: {
      label: '区分',
      type: 'select',
      options: entries(behaviors).map(([k, v]) => v.label),
      validations: { required: true },
    },
    type: {
      label: '費目',
      type: 'select',
      options: entries(types).map(([k, v]) => v.label),
      validations: { required: true },
    },
    description: {
      label: '内容',
      type: 'string',
      validations: { required: true },
    },
    amount: {
      label: '金額',
      type: 'integer',
      validations: { required: true },
    },
  };

  const handleImport = async (rows) => {
    const behaviorsByLabel = fromPairs(entries(behaviors).map(([k, v]) => [v.label, k]));
    const typesByLabel = fromPairs(entries(types).map(([k, v]) => [v.label, k]));
    // 旧ラベル（例: 「楽天ショップ」）でエクスポートされたCSVも読み込めるようにする
    const ownerTypesByLabel = fromPairs(entries(ownerTypes).flatMap(([k, v]) =>
      [[v.label, k], ...(v.legacyLabels || []).map(_ => [_, k])]
    ));

    await batch(rows, (batchWrite, _) => {
      const expenseRef = doc(collection(tenant.ref, 'expenses'));
      batchWrite.set(expenseRef, {
        ownerType: ownerTypesByLabel[_.ownerType] || _.ownerType,
        ownerId: _.ownerId,
        behavior: behaviorsByLabel[_.behavior] || _.behavior,
        type: typesByLabel[_.type] || _.type,
        description: _.description,
        amount: parseInt(_.amount, 10),
        month,
        createdAt: new Date(),
      });
    });
  };

  return (
    <div className="expenses container-fluid">
      <div className="px-4">
        <div className="card p-4">
          <ExpenseTabs activeTab="expenses" />
          <div className="mb-2 d-flex justify-content-start align-items-end gap-1">
            <QueryDateSelector label="年月" dateFormat={'yyyy-MM'} defaultValue={startOfMonth(new Date())} paramName="month" pickerProps={{ showMonthYearPicker: true, showFullMonthYearPicker: true, }} />
          </div>
          <div className="mb-3 d-flex justify-content-start align-items-end gap-1">
            <AddButton processValues={_ => ({ ..._, month, })} itemRef={doc(collection(tenant.ref, 'expenses'))} FormModal={ModelFormModal} formProps={{ title: '追加', fields: fields({ sellers, shops }), }} />
            <ExportButton
              label={false}
              outline
              fileName={`経費_${month}.csv`}
              rows={getExportData}
            />
            <ImportMappingButton
              label={false}
              fields={importFields}
              onImport={handleImport}
              storageKey={`expenses-import-mapping-${tenant.id}`}
            />
          </div>
          <table className="table table-bordered">
            <thead className="thead-light text-nowrap">
              <tr>
                <th style={{ minWidth: 70 }}></th>
                <th style={{ minWidth: 120 }}>種別</th>
                <th style={{ minWidth: 180 }}>ショップ</th>
                <th style={{ minWidth: 100 }}>区分</th>
                <th style={{ minWidth: 180 }}>費目</th>
                <th style={{ minWidth: 250 }}>内容</th>
                <th style={{ minWidth: 150 }}>金額</th>
              </tr>
            </thead>
            <tbody>
              {
                sortedExpenses.map(({ id, ref, ownerType, ownerId, behavior, type, description, amount, }) => {
                  return (
                    <tr key={id} id={id}>
                      <td>
                        <div className="text-nowrap d-flex justify-content-end gap-1">
                          <EditButton itemRef={ref} FormModal={ModelFormModal} formProps={{ title: '編集', fields: fields({ sellers, shops }), }} />
                          <DeleteButton itemRef={ref} />
                        </div>
                      </td>
                      <td>
                        {ownerTypes[ownerType]?.label}
                      </td>
                      <td>
                        {ownersById[ownerId]?.name}
                      </td>
                      <td>
                        {behaviors[behavior]?.label}
                      </td>
                      <td>
                        {types[type]?.label}
                      </td>
                      <td>
                        {description}
                      </td>
                      <td className="text-end">
                        {numeral(amount).format()}円
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
